/**
 * Colis à la loge — the shared parcel vocabulary.
 *
 * Pure and dependency-free (zod aside) so the API, the worker, the console and
 * the mobile app agree on one set of rules rather than four re-implementations.
 *
 * The one rule worth stating out loud: a `remis` parcel names its collector
 * EITHER as a roster member (`handedToUserId`) OR as free text
 * (`handedToLabel`), never both and never neither. Hand-over is trust-based —
 * there is no pickup code — so « qui a récupéré ce colis » is the only thing
 * the record can be asked afterwards, and an ambiguous answer would make the
 * record worthless. The exactly-one rule lives HERE, not in a DB CHECK,
 * because `handed_to_user_id` is ON DELETE SET NULL: a deleted account would
 * otherwise make the constraint fail the deletion rather than the write.
 */
import { z } from "zod/v4";

import type { Terms } from "./terms";

export const PARCEL_STATUSES = ["en_loge", "remis"] as const;
export type ParcelStatus = (typeof PARCEL_STATUSES)[number];
export const parcelStatusSchema = z.enum(PARCEL_STATUSES);

/**
 * Status badge label, vertical-aware: « À la loge » / « À l'accueil » for
 * `en_loge`, « Remis » (no desk noun, so no vertical variation) for `remis`.
 * A bare `Record<ParcelStatus, string>` can't say this — it would hardcode
 * « loge » for every org, including offices.
 */
export function parcelStatusLabel(status: ParcelStatus, terms: Terms): string {
  return status === "en_loge" ? terms.atTheDeskStatusLabel : "Remis";
}

/** Column widths, shared so the keyboard stops where the server would. */
export const PARCEL_CARRIER_MAX = 40;
export const PARCEL_DESCRIPTION_MAX = 200;
export const PARCEL_HANDED_TO_LABEL_MAX = 80;

/**
 * One reminder, at three days. A product decision, not a setting: a
 * configurable delay would need a settings screen, a per-org column and a
 * migration to answer a question no résidence has asked.
 */
export const PARCEL_REMINDER_AFTER_DAYS = 3;

export const parcelDepositInput = z.object({
  unitId: z.string().min(1),
  /** Free text — « Amana », « Glovo ». Never an enum: the courier's name is
   *  whatever is printed on the van, and a closed list would be wrong within
   *  a month. */
  carrier: z.string().trim().min(1).max(PARCEL_CARRIER_MAX).optional(),
  description: z.string().trim().min(1).max(PARCEL_DESCRIPTION_MAX).optional(),
});
export type ParcelDepositInput = z.infer<typeof parcelDepositInput>;

export const parcelHandOverInput = z
  .object({
    parcelId: z.string().min(1),
    handedToUserId: z.string().min(1).optional(),
    handedToLabel: z
      .string()
      .trim()
      .min(1)
      .max(PARCEL_HANDED_TO_LABEL_MAX)
      .optional(),
  })
  // `.trim().min(1)` above is what makes « Autre personne » with an empty
  // field fail the rule below instead of storing a blank collector.
  .refine(
    (v) => (v.handedToUserId === undefined) !== (v.handedToLabel === undefined),
    {
      message:
        "Indiquez qui a récupéré le colis : un membre du foyer ou un nom.",
    },
  );
export type ParcelHandOverInput = z.infer<typeof parcelHandOverInput>;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whole days a parcel has been waiting, floored, never negative.
 *
 * Computed at READ time from `received_at` on every surface, so the amber
 * marker does not depend on the reminder sweep having run — a worker that was
 * down for a day must not make a five-day-old parcel look fresh.
 */
export function parcelAgeDays(
  receivedAt: Date | string,
  now: Date | number,
): number {
  const then = new Date(receivedAt).getTime();
  const at = now instanceof Date ? now.getTime() : now;
  return Math.max(0, Math.floor((at - then) / DAY_MS));
}

/** Amber from day three — the same threshold the reminder fires on, so the
 *  list and the push never disagree about what « qui traîne » means. */
export function isParcelStale(ageDays: number): boolean {
  return ageDays >= PARCEL_REMINDER_AFTER_DAYS;
}

/** « aujourd'hui » / « hier » / « depuis 4 j » — the badge on every list. */
export function parcelSinceLabel(ageDays: number): string {
  if (ageDays <= 0) return "aujourd'hui";
  if (ageDays === 1) return "hier";
  return `depuis ${ageDays} j`;
}

interface CollectorRow {
  /** The roster member's current name, resolved at read time. */
  handedToName: string | null;
  handedToLabel: string | null;
}

/** The collector as a bare name for a table cell or a list row. */
export function parcelCollectorName(r: CollectorRow): string | null {
  return r.handedToName ?? r.handedToLabel ?? null;
}

/**
 * The collector as it reads inside a sentence: « à Sara » for a roster
 * member, « (la femme de ménage) » for a typed label — the parentheses say
 * out loud that no account was named, which is the whole difference between
 * an attributable hand-over and the gardien's word for it.
 *
 * « au foyer » is the fallback for a `remis` row whose collector account has
 * since been deleted (`ON DELETE SET NULL`). It is a real state, so it gets
 * real French rather than an empty string that would leave a push reading
 * « Colis remis  · A 01 ».
 */
export function parcelCollectorPhrase(r: CollectorRow): string {
  if (r.handedToName) return `à ${r.handedToName}`;
  if (r.handedToLabel) return `(${r.handedToLabel})`;
  return "au foyer";
}
